import React, { useContext, useEffect, useState } from 'react'
import { ExitOrEntry } from './style'
import { movementHeaders, movementFields } from './utils'
import { format } from 'date-fns'
import axios from 'axios'
import StockContext from '../../../../contexts/StockContext'
import TableFiltersContext from '../../../../contexts/TableFiltersContext'
import undo from '../../../../assets/undo.svg'
import UserContext from '../../../../contexts/UserContext'
import SectionContext from '../../../../contexts/SectionContext'
import { ArrowDownCircle, ArrowUpCircle } from 'lucide-react'
import { useOutletContext } from 'react-router-dom'
import { StockOutletType } from '..'
import { ConfigButton, GlobalTable, HeaderLine, Td, TdButtons, Th, Tr } from '../../../../components/styles/table-style'
import { TypeMainOutlet } from '../..'
import { Settings } from 'lucide-react'
import EntryOrExit from '../../../../components/ModalEntryOrExit'

interface StockMovement {
  id: number
  rawMaterialName: string
  ploomesOrderId: number
  status: string
  quantity: number
  oldTotalWeight: number
  oldLengthInMm: number
  newTotalWeight: number
  newLengthInMm: number
  orderName: string
  createdAt: string
  updatedAt: string
  updatedBy: any
}

const MovementTable: React.FC = () => {
  const { user } = useContext(UserContext)
  const [permissions, setPermissions] = useState<string[]>([])
  const { details } = useContext(StockContext)
  const { movementFilters, globalFilter, setMovementFilters } = useContext(TableFiltersContext)
  const [stockMovements, setStockMovements] = useState<StockMovement[]>([])
  const [count, setCount] = useState<number>(0)
  const { movementTypeFilter } = useContext(TableFiltersContext);
  const { setTabIndex, fields, setIsDropdownOpen, quantity, setQuantity, refresh, setRefresh } = useOutletContext<StockOutletType>()
  const { search } = useOutletContext<TypeMainOutlet>()
  const { setStockSection } = useContext(SectionContext)
  const [buttonLocker, setButtonLocker] = useState<boolean>(false)

  useEffect(() => {
    setTabIndex(1)
    setMovementFilters([])
    setStockSection('movement')
    setQuantity(8)
  }, [])
  
  useEffect(() => {
    const parameters = {
      size: quantity,
      filters: movementFilters,
      global: search,
      movementType: movementTypeFilter
    }

    const fetchData = () => {
      axios
        .get(`${process.env.REACT_APP_BASE_URL}/stock-movement/find`, {
          params: parameters,
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        })
        .then((response) => {
          setStockMovements(response.data)
        })
        .catch((error) => {
          console.error(
            'Ocorreu um erro ao buscar os dados de movimento de estoque',
            error,
          )
        })
    }

    const timeoutId = setTimeout(fetchData, 200)

    return () => clearTimeout(timeoutId)
  }, [quantity, movementFilters, search, count, refresh, movementTypeFilter])

  useEffect(() => {
    const fetchPermissions = async () => {
      const perms = await getPermissions()
      setPermissions(perms)
    }

    if (user.profileId) {
      fetchPermissions()
    }
  }, [user.profileId])

  const getSaleNumber = (orderName: string) => {
    const regex = /n[°º]\s?da venda:\s?(\d+)/i
    const match = orderName.match(regex)

    if (match) {
      return match[1]
    }

    return orderName.slice(-4)
  }

  const revertMovement = async (ploomesId: number) => {
    setButtonLocker(true)
    try {
      const response = await axios.patch(
        `${process.env.REACT_APP_BASE_URL}/reverse-stock-movement/${ploomesId}`,
        { userId: user.sub },
      )
      if (response.data) {
        setCount(count + response.data)
      }
      setRefresh(prev => !prev)
      setButtonLocker(false)
      
    } catch (error: any) {
      if (error.response.status === 404) {
        alert(
          'Por favor, verifique na Ploomes se este negócio está nos conformes.',
        )
        setButtonLocker(false)
        setRefresh(prev => !prev)

      } else {
        console.error('Erro ao desfazer movimentação', error)
        setButtonLocker(false)
        setRefresh(prev => !prev)

      }
    } finally {
      setCount((prevCount) => prevCount + 1)
    }
  }

  let tableClass = ''
  if (movementFilters.length !== 0) {
    if (details) {
      tableClass = 'filteredTable'
    } else {
      tableClass = 'filteredTable shortTable'
    }
  } else {
    if (!details) {
      tableClass = 'shortTable'
    }
  }

  const formatNumber = (num: number) => {
    return num.toFixed(2)
  }

  const getCellClass = (field: string) => {
    if (field.includes('Weight')) return 'weight-cell'
    if (field.includes('LengthInMm')) return 'length-cell'
    if (field === 'quantity') return 'quantity-cell'
    return ''
  }

  const handleIdClick = (id: number) => {
    window.open(`https://app10.ploomes.com/deal/${id}`, '_blank')
  }

  const getPermissions = async () => {
    try {
      const response = await axios(
        `${process.env.REACT_APP_BASE_URL}/profile/find/${user.profileId}`,
      )

      if (response.data) {
        console.log(response.data)
        return response.data.permissions
      }
    } catch (error: any) {
      console.log('Usuário sem perfil')
    }
  }

  const renderOrderCell = (item: StockMovement) => {
    const { ploomesOrderId, orderName } = item
    const maxLength = 25; // Defina o número máximo de caracteres desejado
    const truncatedOrderName = orderName.length > maxLength 
      ? `${orderName.slice(0, maxLength)}...` 
      : orderName;

    if (ploomesOrderId === 0) {
      return (
        <ExitOrEntry title='Entrada' className="flex items-center gap-2 entry">
          <ArrowDownCircle color="green" size={20} />
          <span className='inline-flex' title={orderName}>{truncatedOrderName}</span>
        </ExitOrEntry>
      )
    }

    if (ploomesOrderId === 1) {
      return (
        <ExitOrEntry title='Saída'>
          <ArrowUpCircle color='red' size={20} />
          <span title={orderName}>{truncatedOrderName}</span>
        </ExitOrEntry>
      )
    }

    return (
      <span
        title={orderName}
        onClick={() => handleIdClick(ploomesOrderId)}
        style={{ cursor: 'pointer', color: 'blue' }}
      >
        {orderName.length > 25
          ? `${getSaleNumber(orderName)} - ${orderName.substring(0, 25)}...`
          : getSaleNumber(orderName)}
      </span>
    )
  }

  const filteredData = (item: StockMovement) => {
    if (movementTypeFilter === "Manual") {
      return item.ploomesOrderId === 0 || item.ploomesOrderId === 1;
    }
    if (movementTypeFilter === "Pedido") {
      return item.ploomesOrderId !== 0 && item.ploomesOrderId !== 1;
    }
    return true; // Se nenhum filtro estiver ativo, mostra tudo
  };

  return (
    <GlobalTable>
      <thead>
        <HeaderLine>
          {fields.whichFieldsToShow.filter(field => field.checked).map((field, index) => (
            <Th key={index}>{field.fieldName}</Th>
          ))}
          <Th></Th>
          <ConfigButton onClick={() => setIsDropdownOpen(prev => !prev)}><Settings/></ConfigButton>
        </HeaderLine>
      </thead>
      <tbody>
        {stockMovements.filter(filteredData).map((item) => (
          <Tr
            key={item.id}
            className={item.status === '410' ? 'yellowLine' : ''}
            title={
    item.updatedBy?.name && item.updatedAt 
      ? `Atualizado por ${item.updatedBy.name} em ${
        format(new Date(item.updatedAt), 'dd/MM/yyyy - HH:mm')}h`
      : undefined
            }
          >
            {fields.whichFieldsToShow.filter(field => field.checked).map((field, index) => (
              <Td key={index} className={getCellClass(field.field)}>
                {field.field === 'createdAt' ? (
                  format(new Date(item.createdAt), 'dd/MM/yyyy - HH:mm') + 'h'
                ) : field.field === 'ploomesOrderId' ? (
                  renderOrderCell(item)
                ) : typeof item[field.field as keyof StockMovement] === 'number' ? (
                  formatNumber(item[field.field as keyof StockMovement] as number)
                ) : field.field === 'status' ? (
                  item[field.field] === '410' ? (
                    <>Desfeito</>
                  ) : (
                    <>Abatido</>
                  )
                ) : (
                  item[field.field as keyof StockMovement]
                )}
              </Td>
            ))}
              {item.status !== '410' && permissions.includes('edit-stock') ? (
                
                <TdButtons>
                  {buttonLocker ?
                <></>
                :
                <TdButtons>
                  <img
                    title='Desfazer'
                    src={undo}
                    alt='undo'
                    className='undo-button'
                    draggable='false'
                    onClick={() => revertMovement(item.ploomesOrderId)}
                    style={{cursor: 'pointer'}}
                  />
                </TdButtons>
                }
                  
                </TdButtons>
              ) 
              : <span></span>}
          </Tr>
        ))}
      </tbody>
    </GlobalTable>
  )
}

export default MovementTable